import type { AiContextAttachment } from "@productlane/lib/src/ai"

import { contextMentionText } from "./context-mention"

export type ContextMentionSegment =
	| { kind: "text"; text: string }
	| { kind: "mention"; attachment: AiContextAttachment }

// The sent message keeps its mentions as plain "@Label" text, so the transcript
// rebuilds the badges by matching the turn's own attachments against it.
export const splitContextMentionText = (
	content: string,
	attachments: readonly AiContextAttachment[] | undefined,
): ContextMentionSegment[] => {
	if (!content) return []
	if (!attachments || attachments.length === 0) {
		return [{ kind: "text", text: content }]
	}

	// Longest first, so "@PRO-1 Ship it" never loses to a shorter overlap.
	const needles = attachments
		.map((attachment) => ({ attachment, text: contextMentionText(attachment) }))
		.filter((entry) => entry.text.length > 1)
		.sort((a, b) => b.text.length - a.text.length)

	const segments: ContextMentionSegment[] = []
	let buffer = ""
	let cursor = 0

	const flush = () => {
		if (!buffer) return
		segments.push({ kind: "text", text: buffer })
		buffer = ""
	}

	while (cursor < content.length) {
		const hit =
			content[cursor] === "@"
				? needles.find((entry) => content.startsWith(entry.text, cursor))
				: undefined
		if (hit) {
			flush()
			segments.push({ kind: "mention", attachment: hit.attachment })
			cursor += hit.text.length
			continue
		}
		buffer += content[cursor]
		cursor += 1
	}

	flush()
	return segments
}

export const hasContextMentionSegments = (
	segments: ContextMentionSegment[],
): boolean => segments.some((segment) => segment.kind === "mention")
